import type { UseQueryOptions, UseQuerySharedOptions } from '@core/types'
import type { Consent } from '@spilnu/content'
import type { MaybeRefOrGetter } from '#imports'

/**
 * UK-layer override of nimbus useStrapiConsent.
 * Same architecture as usePageData: synchronous composable, all dependencies
 * initialized at top level, both content and Strapi queries created upfront
 * with `enabled` flags. No async, no conditional composable calls.
 *
 * Per-collection gating via `useContentMigrationFlag('<key>')`:
 *  - getGeneralTerms → 'generalConsents'
 *  - getPrivacyPolicy / getType → 'consents'
 */

function findByType(consents: Consent[], type: string): Consent | null {
    const matches = consents.filter(c => c.type === type)
    if (matches.length <= 1) return matches[0] ?? null
    return matches.sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0))[0] ?? null
}

export const useStrapiConsent = () => {
    const { site } = useCoreConfig()
    const client = useStrapi()
    const { getGeneralConsents } = useContentGeneralConsent()
    const { getConsents } = useContentConsent()

    function getGeneralTerms(options: Pick<UseQuerySharedOptions, 'enabled'> & Pick<UseQueryOptions<unknown>, 'lazy' | 'server'> = {}) {
        const { enabled = true, lazy = true, server = false } = options

        const contentQuery = useQuery(async () => {
            const consents = await getGeneralConsents()
            return consents?.[0] ?? null
        }, {
            key: 'content:general-consent',
            lazy,
            server,
            enabled: () => useContentMigrationFlag('generalConsents') && toValue(enabled),
        })

        const strapiQuery = useQuery(async () => {
            const { data } = await client.GET('/general-consents', {
                params: { query: { brand: site.brand } },
            })
            return data
        }, {
            key: 'strapi:general-consent',
            lazy,
            server,
            enabled: () => !useContentMigrationFlag('generalConsents') && toValue(enabled),
        })

        if (useContentMigrationFlag('generalConsents')) return contentQuery
        return strapiQuery
    }

    function getPrivacyPolicy(options: Pick<UseQuerySharedOptions, 'enabled'> & Pick<UseQueryOptions<unknown>, 'lazy' | 'server'> = {}) {
        const { enabled = true, lazy = true, server = false } = options

        const contentQuery = useQuery(async () => {
            const consents = (await getConsents()) as Consent[]
            return findByType(consents, 'privacy-policy')
        }, {
            key: 'content:consent:privacy-policy',
            lazy,
            server,
            enabled: () => useContentMigrationFlag('consents') && toValue(enabled),
        })

        const strapiQuery = useQuery(async () => {
            const { data } = await client.GET('/consents/{type}', {
                params: {
                    path: { type: 'privacy-policy' },
                    query: { brand: site.brand },
                },
            })
            return data
        }, {
            key: 'strapi:consent:privacy-policy',
            lazy,
            server,
            enabled: () => !useContentMigrationFlag('consents') && toValue(enabled),
        })

        if (useContentMigrationFlag('consents')) return contentQuery
        return strapiQuery
    }

    function getType(options: Pick<UseQuerySharedOptions, 'enabled'> & Pick<UseQueryOptions<unknown>, 'lazy' | 'server' | 'immediate'> & { type: MaybeRefOrGetter<string> }) {
        const { type, enabled = true, lazy = true, server = false } = options

        const contentQuery = useQuery(async () => {
            const consents = (await getConsents()) as Consent[]
            return findByType(consents, toValue(type))
        }, {
            key: () => `content:consent:${toValue(type)}`,
            lazy,
            server,
            enabled: () => useContentMigrationFlag('consents') && toValue(enabled),
        })

        const strapiQuery = useQuery(async () => {
            const { data } = await client.GET('/consents/{type}', {
                params: {
                    path: { type: toValue(type) },
                    query: { brand: site.brand },
                },
            })
            return data
        }, {
            key: () => `strapi:consent:${toValue(type)}`,
            lazy,
            server,
            enabled: () => !useContentMigrationFlag('consents') && toValue(enabled),
        })

        if (useContentMigrationFlag('consents')) return contentQuery
        return strapiQuery
    }

    return { getGeneralTerms, getPrivacyPolicy, getType }
}
